import { useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Star } from "lucide-react";
import { cn } from "@/lib/utils";

interface RatingDialogProps {
  trigger: React.ReactNode;
  onSubmit: (rating: number, comment: string) => Promise<void>;
  title?: string;
  description?: string;
  isSubmitting?: boolean;
  defaultRating?: number;
  defaultComment?: string;
}

export const RatingDialog = ({
  trigger,
  onSubmit,
  title,
  description,
  isSubmitting,
  defaultRating = 0,
  defaultComment = "",
}: RatingDialogProps) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [rating, setRating] = useState(defaultRating);
  const [hoveredRating, setHoveredRating] = useState(0);
  const [comment, setComment] = useState(defaultComment);

  const handleSubmit = async () => {
    if (rating === 0) return;
    await onSubmit(rating, comment);
    setOpen(false);
    setRating(0);
    setComment("");
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>{trigger}</DialogTrigger>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle className="text-2xl font-['Lexend_Deca'] text-[#37699E]">
            {title || t('rating.title')}
          </DialogTitle>
          <DialogDescription className="text-[#5A6B7A]">
            {description || t('rating.question')}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6 py-4">
          {/* Star Rating */}
          <div className="flex justify-center gap-2">
            {[1, 2, 3, 4, 5].map((star) => (
              <button
                key={star}
                type="button"
                onClick={() => setRating(star)}
                onMouseEnter={() => setHoveredRating(star)}
                onMouseLeave={() => setHoveredRating(0)}
                className="transition-transform hover:scale-110 focus:outline-none focus:scale-110"
              >
                <Star
                  className={cn(
                    "w-10 h-10 transition-colors",
                    (hoveredRating >= star || rating >= star)
                      ? "fill-[#FFBF07] text-[#FFBF07]"
                      : "text-[#DBE2EA]"
                  )}
                />
              </button>
            ))}
          </div>

          {/* Rating label */}
          {rating > 0 && (
            <p className="text-center text-sm text-[#5A6B7A] animate-in fade-in">
              {rating === 5 && t('rating.excellent')}
              {rating === 4 && t('rating.veryGood')}
              {rating === 3 && t('rating.good')}
              {rating === 2 && t('rating.fair')}
              {rating === 1 && t('rating.poor')}
            </p>
          )}

          {/* Comment */}
          <div className="space-y-2">
            <label className="text-xs font-medium text-[#5A6B7A] uppercase tracking-wide">
              {t('rating.comment')}
            </label>
            <Textarea
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              placeholder={t('rating.commentPlaceholder')}
              className="min-h-[100px] resize-none"
              maxLength={500}
            />
            <p className="text-xs text-[#87ADD4] text-right">
              {comment.length}/500
            </p>
          </div>

          {/* Submit Button */}
          <Button
            onClick={handleSubmit}
            disabled={rating === 0 || isSubmitting}
            className="w-full bg-[#FFBF07] hover:bg-[#E5AB06] text-[#2D3748] font-semibold"
          >
            {isSubmitting ? t('rating.submitting') : t('rating.submit')}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
